import {
  ChevronDown,
  FilePlus,
  ListChecks,
  Mic,
  Phone,
  PhoneIncoming,
  PhoneMissed,
  PhoneOutgoing,
  Plus,
  Search,
  UserX,
  Users,
  Voicemail,
} from "lucide-react";
import { useTranslations } from "next-intl";
import type { ReactNode } from "react";
import { H, MODULE_HEIGHT, SIDEBAR_WIDTH } from "../../hero-tokens";
import type { ModuleProps } from "../module-registry";
import {
  type TelefonCall,
  type TelefonCallStatusIcon,
  telefonCalls,
} from "./module-telefon-data";

/** Full-detail call rows in the marketing mock; the rest fade to skeleton bars. */
const TELEFON_CALLS_FULL_DETAIL_COUNT = 2;

function SkeletonBar(props: {
  readonly width: number | string;
  readonly height?: number;
}): ReactNode {
  return (
    <span
      style={{
        display: "block",
        width: props.width,
        height: props.height ?? 8,
        borderRadius: 4,
        background: "#ECEEF1",
      }}
    />
  );
}

function CallRowSkeleton(): ReactNode {
  return (
    <div
      style={{
        display: "flex",
        alignItems: "center",
        gap: 13,
        padding: "12px 6px",
        borderBottom: "1px solid #F1F2F4",
      }}
    >
      <span
        style={{
          width: 44,
          height: 44,
          borderRadius: 11,
          background: "#ECEEF1",
          flexShrink: 0,
        }}
      />
      <div
        style={{
          flex: 1,
          minWidth: 0,
          display: "flex",
          flexDirection: "column",
          gap: 6,
        }}
      >
        <SkeletonBar width="38%" height={10} />
        <SkeletonBar width="24%" height={8} />
      </div>
      <div style={{ width: 300, flexShrink: 0 }}>
        <SkeletonBar width="62%" height={8} />
      </div>
      <SkeletonBar width={52} height={8} />
      <span
        style={{
          width: 26,
          height: 26,
          borderRadius: "50%",
          background: "#ECEEF1",
          flexShrink: 0,
        }}
      />
    </div>
  );
}

function StatusIcon({
  icon,
}: { readonly icon: TelefonCallStatusIcon }): ReactNode {
  if (icon === "phone-outgoing") {
    return <PhoneOutgoing width={14} height={14} strokeWidth={1.9} />;
  }
  if (icon === "phone-missed") {
    return <PhoneMissed width={14} height={14} strokeWidth={1.9} />;
  }
  return <PhoneIncoming width={14} height={14} strokeWidth={1.9} />;
}

function TelefonSidebar({
  setInCall,
}: { readonly setInCall: (value: boolean) => void }): ReactNode {
  const t = useTranslations("marketing.moduleTelefon");
  return (
    <div
      style={{
        width: SIDEBAR_WIDTH,
        borderRight: "1px solid #EEF0F3",
        background: "#FBFBFC",
        padding: "16px 12px",
        boxSizing: "border-box",
      }}
    >
      <div
        style={{
          display: "flex",
          alignItems: "center",
          justifyContent: "space-between",
          padding: "0 6px 12px",
        }}
      >
        <span style={{ fontSize: 16, fontWeight: 700 }}>{t("telefon")}</span>
      </div>
      <div
        style={{
          display: "flex",
          alignItems: "center",
          gap: 8,
          height: 34,
          padding: "0 11px",
          background: "#F1F2F4",
          borderRadius: 9,
          color: H.inkDim,
          fontSize: 13,
          marginBottom: 14,
        }}
      >
        <Search width={15} height={15} strokeWidth={1.8} />
        {t("suchen")}
      </div>
      <div style={{ display: "flex", flexDirection: "column", gap: 2 }}>
        <div
          style={{
            display: "flex",
            alignItems: "center",
            gap: 10,
            padding: "7px 9px",
            borderRadius: 8,
            fontSize: 13.5,
            color: H.inkSoft,
          }}
        >
          <span
            style={{
              width: 22,
              height: 22,
              borderRadius: "50%",
              background: H.ink,
              color: "#fff",
              display: "inline-flex",
              alignItems: "center",
              justifyContent: "center",
              fontSize: 9,
              fontWeight: 700,
              flexShrink: 0,
            }}
          >
            {t("nh")}
          </span>
          <span style={{ flex: 1 }}>{t("meineAnrufe")}</span>
        </div>
        <div
          style={{
            display: "flex",
            alignItems: "center",
            gap: 10,
            padding: "7px 9px",
            borderRadius: 8,
            fontSize: 13.5,
            fontWeight: 600,
            color: H.accent,
            background: H.accentSoft,
          }}
        >
          <Users width={16} height={16} strokeWidth={1.9} />
          <span style={{ flex: 1 }}>{t("alleAnrufe")}</span>
        </div>
        <div
          style={{
            display: "flex",
            alignItems: "center",
            gap: 10,
            padding: "7px 9px",
            borderRadius: 8,
            fontSize: 13.5,
            color: H.inkSoft,
          }}
        >
          <span style={{ color: "#6B7280", display: "inline-flex" }}>
            <UserX width={16} height={16} strokeWidth={1.8} />
          </span>
          <span style={{ flex: 1 }}>{t("unzugewiesen")}</span>
        </div>
      </div>
      <div
        style={{
          fontFamily: H.label,
          fontSize: 10,
          letterSpacing: "0.12em",
          color: H.inkGhost,
          textTransform: "uppercase",
          padding: "18px 9px 9px",
        }}
      >
        {t("anrufen")}
      </div>
      <div
        style={{ border: "1px solid #ECEEF1", borderRadius: 11, padding: 11 }}
      >
        <div style={{ display: "flex", gap: 10, alignItems: "center" }}>
          <span
            style={{
              width: 30,
              height: 30,
              borderRadius: 8,
              background: H.accentSoft,
              color: H.accent,
              display: "inline-flex",
              alignItems: "center",
              justifyContent: "center",
              flexShrink: 0,
            }}
          >
            <PhoneOutgoing width={15} height={15} strokeWidth={1.9} />
          </span>
          <div style={{ minWidth: 0, flex: 1 }}>
            <div style={{ fontSize: 12.5, fontWeight: 600, color: H.ink }}>
              {t("zentrale")}
            </div>
            <div
              style={{
                fontSize: 11,
                color: H.inkDim,
                fontVariantNumeric: "tabular-nums",
                whiteSpace: "nowrap",
                overflow: "hidden",
                textOverflow: "ellipsis",
              }}
            >
              {t("k492419843")}
            </div>
          </div>
          <ChevronDown
            width={14}
            height={14}
            strokeWidth={1.8}
            style={{ color: "rgba(11,15,25,0.3)", flexShrink: 0 }}
          />
        </div>
        <div
          style={{
            display: "flex",
            alignItems: "center",
            gap: 8,
            height: 36,
            padding: "0 11px",
            background: "#F6F7F9",
            border: "1px solid #ECEEF1",
            borderRadius: 9,
            color: H.inkDim,
            fontSize: 12.5,
            marginTop: 10,
          }}
        >
          <Phone width={14} height={14} strokeWidth={1.8} />
          {t("nummerKontakt")}
        </div>
        <div
          onClick={() => setInCall(true)}
          style={{
            display: "flex",
            alignItems: "center",
            justifyContent: "center",
            gap: 7,
            height: 38,
            background: H.accent,
            color: "#fff",
            borderRadius: 9,
            fontSize: 13,
            fontWeight: 600,
            marginTop: 8,
            cursor: "pointer",
          }}
        >
          <Phone width={14} height={14} strokeWidth={2} />
          {t("anrufen2")}
        </div>
      </div>
      <div
        style={{
          display: "flex",
          alignItems: "center",
          justifyContent: "space-between",
          fontFamily: H.label,
          fontSize: 10,
          letterSpacing: "0.12em",
          color: H.inkGhost,
          textTransform: "uppercase",
          padding: "18px 9px 8px",
        }}
      >
        <span>{t("kurzwahl")}</span>
        <span
          style={{
            display: "inline-flex",
            alignItems: "center",
            gap: 4,
            color: H.inkDim,
            cursor: "pointer",
          }}
        >
          <Plus width={12} height={12} strokeWidth={2} />
          {t("hinzufuegen")}
        </span>
      </div>
      <div
        style={{
          display: "flex",
          alignItems: "center",
          gap: 10,
          padding: "7px 9px",
          borderRadius: 8,
          fontSize: 13.5,
          color: H.inkSoft,
        }}
      >
        <span
          style={{
            width: 26,
            height: 26,
            borderRadius: "50%",
            background: H.accentSoft,
            color: H.accent,
            display: "inline-flex",
            alignItems: "center",
            justifyContent: "center",
            flexShrink: 0,
          }}
        >
          <Phone width={13} height={13} strokeWidth={1.9} />
        </span>
        <span
          style={{
            flex: 1,
            whiteSpace: "nowrap",
            overflow: "hidden",
            textOverflow: "ellipsis",
          }}
        >
          {t("hausmeisterServiceNord")}
        </span>
      </div>
    </div>
  );
}

function CallRow({ c }: { readonly c: TelefonCall }): ReactNode {
  const t = useTranslations("marketing.moduleTelefon");
  return (
    <div
      style={{
        display: "flex",
        alignItems: "center",
        gap: 13,
        padding: "12px 6px",
        borderBottom: "1px solid #F1F2F4",
      }}
    >
      <span
        style={{
          width: 44,
          height: 44,
          borderRadius: 11,
          background: c.boxBg,
          color: c.boxFg,
          display: "inline-flex",
          alignItems: "center",
          justifyContent: "center",
          fontSize: 13,
          fontWeight: 600,
          flexShrink: 0,
        }}
      >
        {c.box}
      </span>
      <div style={{ flex: 1, minWidth: 0 }}>
        <div style={{ fontSize: 14, color: H.ink }}>
          <span style={{ fontWeight: 600 }}>{c.name}</span>{" "}
          <span style={{ color: H.inkDim }}>· {c.number}</span>
        </div>
        <div
          style={{
            display: "flex",
            alignItems: "center",
            gap: 6,
            marginTop: 3,
            fontSize: 12.5,
          }}
        >
          {c.isVm ? (
            <>
              <span
                style={{
                  display: "inline-flex",
                  alignItems: "center",
                  gap: 5,
                  color: H.accent,
                  fontWeight: 500,
                  cursor: "pointer",
                }}
              >
                <Voicemail width={14} height={14} strokeWidth={1.9} />
                {t("nachrichtAbspielen")}
              </span>
              <span style={{ color: "rgba(11,15,25,0.3)" }}>·</span>
              <span style={{ color: "#C0453E" }}>{t("nichtErreicht")}</span>
            </>
          ) : c.statusIcon !== "" ? (
            <span
              style={{
                display: "inline-flex",
                alignItems: "center",
                gap: 5,
                color: c.statusColor,
              }}
            >
              <StatusIcon icon={c.statusIcon} />
              {c.statusLabel}
            </span>
          ) : null}
        </div>
      </div>
      <div
        style={{
          width: 300,
          flexShrink: 0,
          display: "flex",
          alignItems: "center",
          gap: 10,
        }}
      >
        {c.isVm ? (
          <span
            style={{
              display: "inline-flex",
              alignItems: "center",
              gap: 6,
              minWidth: 0,
            }}
          >
            <Mic
              width={13}
              height={13}
              strokeWidth={1.8}
              style={{ color: "rgba(11,15,25,0.4)", flexShrink: 0 }}
            />
            <span
              style={{
                fontSize: 12.5,
                fontStyle: "italic",
                color: H.inkFaint,
                whiteSpace: "nowrap",
                overflow: "hidden",
                textOverflow: "ellipsis",
              }}
            >
              {c.transcript}
            </span>
          </span>
        ) : (
          <>
            <span
              style={{
                display: "inline-flex",
                alignItems: "center",
                gap: 6,
                fontSize: 12.5,
                color: H.inkDim,
              }}
            >
              <ListChecks width={14} height={14} strokeWidth={1.8} />
              {t("aufgabeErstellen")}
            </span>
            <span
              style={{
                display: "inline-flex",
                alignItems: "center",
                gap: 6,
                fontSize: 12.5,
                color: H.inkDim,
              }}
            >
              <FilePlus width={14} height={14} strokeWidth={1.8} />
              {t("notizErstellen")}
            </span>
          </>
        )}
      </div>
      <span
        style={{
          fontSize: 12,
          color: "rgba(11,15,25,0.4)",
          minWidth: 74,
          textAlign: "right",
          flexShrink: 0,
        }}
      >
        {c.time}
      </span>
      {c.assigned ? (
        <span
          style={{
            width: 26,
            height: 26,
            borderRadius: "50%",
            background: "#E8853A",
            color: "#fff",
            display: "inline-flex",
            alignItems: "center",
            justifyContent: "center",
            fontSize: 9,
            fontWeight: 700,
            flexShrink: 0,
          }}
        >
          {c.assignInit}
        </span>
      ) : null}
      {c.unassigned ? (
        <span
          style={{
            width: 26,
            height: 26,
            borderRadius: "50%",
            border: "1.5px dashed #D7DAE0",
            boxSizing: "border-box",
            flexShrink: 0,
          }}
        />
      ) : null}
    </div>
  );
}

function TelefonContent(): ReactNode {
  const t = useTranslations("marketing.moduleTelefon");
  const tData = useTranslations("marketing.moduleTelefonData");
  return (
    <div style={{ flex: 1, padding: "20px 22px", boxSizing: "border-box" }}>
      <div style={{ position: "relative" }}>
        <div
          style={{
            display: "flex",
            alignItems: "center",
            gap: 4,
            marginBottom: 18,
          }}
        >
          <span
            style={{
              fontSize: 13.5,
              fontWeight: 600,
              color: H.accent,
              background: H.accentSoft,
              padding: "6px 13px",
              borderRadius: 8,
            }}
          >
            {t("telefon")}
          </span>
          <span
            style={{
              fontSize: 13.5,
              fontWeight: 500,
              color: H.inkFaint,
              padding: "6px 13px",
            }}
          >
            {t("anrufeAmpMeetings")}
          </span>
        </div>
        <div
          style={{
            display: "flex",
            alignItems: "center",
            gap: 9,
            marginBottom: 14,
          }}
        >
          <span
            style={{ color: "rgba(11,15,25,0.55)", display: "inline-flex" }}
          >
            <Users width={18} height={18} strokeWidth={1.9} />
          </span>
          <span style={{ fontSize: 18, fontWeight: 700 }}>
            {t("alleAnrufe")}
          </span>
        </div>
        <div
          style={{
            display: "flex",
            alignItems: "center",
            gap: 4,
            background: "#F4F5F7",
            borderRadius: 10,
            padding: 3,
            width: "fit-content",
            whiteSpace: "nowrap",
            marginBottom: 6,
          }}
        >
          <span
            style={{
              background: "#fff",
              borderRadius: 8,
              padding: "6px 13px",
              fontSize: 13,
              fontWeight: 600,
              color: H.ink,
              boxShadow: "0 1px 2px rgba(11,15,25,0.06)",
            }}
          >
            {t("alle")}
          </span>
          <span
            style={{ padding: "6px 13px", fontSize: 13, color: H.inkFaint }}
          >
            {t("verpasst")}
          </span>
          <span
            style={{ padding: "6px 13px", fontSize: 13, color: H.inkFaint }}
          >
            {t("eingehend")}
          </span>
          <span
            style={{ padding: "6px 13px", fontSize: 13, color: H.inkFaint }}
          >
            {t("ausgehend")}
          </span>
          <span
            style={{ padding: "6px 13px", fontSize: 13, color: H.inkFaint }}
          >
            {t("sprachnachricht")}
          </span>
        </div>
        <div>
          {telefonCalls(tData).map((c, i) =>
            i < TELEFON_CALLS_FULL_DETAIL_COUNT ? (
              <CallRow key={`${c.box}-${c.number}-${i}`} c={c} />
            ) : (
              <CallRowSkeleton key={`sk-${i}`} />
            ),
          )}
        </div>
      </div>
    </div>
  );
}

export function ModuleTelefon(props: ModuleProps): ReactNode {
  return (
    <div style={{ display: "flex", height: MODULE_HEIGHT }}>
      <TelefonSidebar setInCall={props.setInCall} />
      <TelefonContent />
    </div>
  );
}
